import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LarsVoiceAgent from './components/LarsVoiceAgent';
import CookieConsent from './components/CookieConsent';
import StickyConversionBar from './components/StickyConversionBar';
import { captureUtm } from './lib/utm';
import { initScrollDepth } from './lib/scrollDepth';

// HomePage eagerly imported — first-paint critical AND avoids a Suspense/Helmet
// race under react-snap pre-render where helmet-async's DOM mutation finished
// after the puppeteer snapshot was taken (resulting in zero <head> tags).
// Other pages remain lazy-loaded below.
import HomePage from './pages/HomePage';
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AanmeldenPage = lazy(() => import('./pages/AanmeldenPage'));
const OffertePage = lazy(() => import('./pages/OffertePage'));
const RittenregistratiePage = lazy(() => import('./pages/RittenregistratiePage'));
const GenericServicePage = lazy(() => import('./pages/GenericServicePage'));
const GpsTrackingPage = lazy(() => import('./pages/GpsTrackingPage'));
const GpsTrackingSubPage = lazy(() => import('./pages/GpsTrackingSubPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));

const PrijzenPage = lazy(() => import('./pages/PrijzenPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BesparingscalculatorPage = lazy(() => import('./pages/BesparingscalculatorPage'));
const CityLandingPage = lazy(() => import('./pages/CityLandingPage'));
const IndustryLandingPage = lazy(() => import('./pages/IndustryLandingPage'));
const LegalPages = lazy(() => import('./pages/LegalPages'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));
const BacklinkToolkitPage = lazy(() => import('./pages/BacklinkToolkitPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const VergelijkingPage = lazy(() => import('./pages/VergelijkingPage'));
const AlternatiefPage = lazy(() => import('./pages/AlternatiefPage'));
const RrsKeurmerkLijst2026Page = lazy(() => import('./pages/RrsKeurmerkLijst2026Page'));
const AvgRittenregistratieWerknemerPage = lazy(() => import('./pages/AvgRittenregistratieWerknemerPage'));
const RittenregistratieZonderKastjePage = lazy(() => import('./pages/RittenregistratieZonderKastjePage'));
const RittenregistratieSjabloonPage = lazy(() => import('./pages/RittenregistratieSjabloonPage'));
const RittenregistratieRegelsIndexPage = lazy(() => import('./pages/RittenregistratieRegelsIndexPage'));
const GpsTrackerZonderAbonnementZakelijkPage = lazy(() => import('./pages/GpsTrackerZonderAbonnementZakelijkPage'));
const WagenparkbeheerKostenPage = lazy(() => import('./pages/WagenparkbeheerKostenPage'));
const BestelwagenGestolenWatNuPage = lazy(() => import('./pages/BestelwagenGestolenWatNuPage'));
const DieselDieftalVoorkomPage = lazy(() => import('./pages/DieselDieftalVoorkomPage'));
const VcaRittenregistratieEisPage = lazy(() => import('./pages/VcaRittenregistratieEisPage'));
const GpsTrackerBouwbedrijfWerknemerAvgPage = lazy(() => import('./pages/GpsTrackerBouwbedrijfWerknemerAvgPage'));
const BestelwagenHurenMetGpsPage = lazy(() => import('./pages/BestelwagenHurenMetGpsPage'));
const GpsTrackerUitbouwenPrivacyPage = lazy(() => import('./pages/GpsTrackerUitbouwenPrivacyPage'));
const ZzpRittenadministratieZonderWerkgeverPage = lazy(() => import('./pages/ZzpRittenadministratieZonderWerkgeverPage'));
const GpsTrackerZonderBijtelling2026Page = lazy(() => import('./pages/GpsTrackerZonderBijtelling2026Page'));
const TrackerInstallerenLeasewagenToestemmingPage = lazy(() => import('./pages/TrackerInstallerenLeasewagenToestemmingPage'));
const GpsDataBewaartermijnPage = lazy(() => import('./pages/GpsDataBewaartermijnPage'));
const WerknemerGpsToestemmingVragenPage = lazy(() => import('./pages/WerknemerGpsToestemmingVragenPage'));
const GpsOnnauwkeurigLocatieFixPage = lazy(() => import('./pages/GpsOnnauwkeurigLocatieFixPage'));
const GpsTrackerZonderExportKostenPage = lazy(() => import('./pages/GpsTrackerZonderExportKostenPage'));
const WpmLanding = lazy(() => import('./pages/WpmLanding'));
const WagenparkdataEigendomDriverPage = lazy(() => import('./pages/WagenparkdataEigendomDriverPage'));
const GpsSchendingWerknemerrechtenClaimPage = lazy(() => import('./pages/GpsSchendingWerknemerrechtenClaimPage'));
const RitgegevensBelastingcontroleAvgPage = lazy(() => import('./pages/RitgegevensBelastingcontroleAvgPage'));
const GpsTrackerVoertuigverkoopVerwijderingPage = lazy(() => import('./pages/GpsTrackerVoertuigverkoopVerwijderingPage'));
const WoordenboekIndexPage = lazy(() => import('./pages/WoordenboekIndexPage'));
const WoordenboekTermPage = lazy(() => import('./pages/WoordenboekTermPage'));
const GpsTrackerIndexPage = lazy(() => import('./pages/GpsTrackerIndexPage'));
const GpsTrackerVehiclePage = lazy(() => import('./pages/GpsTrackerVehiclePage'));
const IgHub = lazy(() => import('./pages/IgHub'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      <p className="text-slate-400 font-medium text-sm">Laden...</p>
    </div>
  </div>
);

const SITE_URL = 'https://www.fleettrackholland.nl';

/**
 * Emits self-reference hreflang for every page automatically.
 * Fixes Ahrefs "Self-reference hreflang annotation missing" (81 pages).
 * Uses useLocation so the URL is always correct for the current route.
 */
function HreflangHead() {
  const { pathname } = useLocation();
  const path = pathname === '/' ? '/' : pathname.endsWith('/') ? pathname : `${pathname}/`;
  const url = `${SITE_URL}${path}`;
  return (
    <Helmet defer={false}>
      <link rel="alternate" hrefLang="nl" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
    </Helmet>
  );
}

// City data for SEO landing pages
const CITIES = [
  'amsterdam', 'rotterdam', 'utrecht', 'den-haag', 'eindhoven',
  'groningen', 'tilburg', 'breda', 'den-bosch', 'nijmegen',
  'arnhem', 'apeldoorn', 'zwolle', 'enschede', 'haarlem',
  'leiden', 'amersfoort', 'almere', 'maastricht', 'zaanstad'
];

// Industry sub-pages
const INDUSTRIES_RITTEN = [
  { slug: 'bouw', name: 'Bouw' },
  { slug: 'taxi', name: 'Taxi' },
  { slug: 'installateurs', name: 'Installateurs' },
  { slug: 'vastgoed', name: 'Vastgoed & Makelaars' },
];

const INDUSTRIES_FLEET = [
  { slug: 'transport', name: 'Transport' },
  { slug: 'koeriers', name: 'Koeriers' },
  { slug: 'service', name: 'Servicebedrijven' },
  { slug: 'thuiszorg', name: 'Thuiszorg' },
  { slug: 'landbouw', name: 'Landbouw & Agri' },
  { slug: 'overheid', name: 'Gemeenten & Overheid' },
  { slug: 'schoonmaak', name: 'Schoonmaakbedrijven' },
  { slug: 'hoveniers', name: 'Hoveniers & Groenvoorziening' },
  { slug: 'verhuizers', name: 'Verhuisbedrijven' },
  { slug: 'afvalbeheer', name: 'Afvalbeheer & Recycling' },
  { slug: 'catering', name: 'Catering & Horeca' },
  { slug: 'farmaceutisch', name: 'Farmaceutisch Transport' },
];

function App() {
  // Growth infra: capture UTM params on landing (P4) and start scroll-depth
  // tracking (P3). Both are prerender-safe no-ops on the server.
  useEffect(() => {
    captureUtm();
    const cleanupScrollDepth = initScrollDepth();
    return cleanupScrollDepth;
  }, []);

  return (
    <LanguageProvider>
      <Router>
          <HreflangHead />
          <ScrollToTop />
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/aanmelden" element={<AanmeldenPage />} />
                  <Route path="/rittenregistratie" element={<RittenregistratiePage />} />
                  {/* Typo/misspelling redirect — prevents 404 for common 1-t variant */}
                  <Route path="/ritregistratie" element={<Navigate to="/rittenregistratie/" replace />} />
                  <Route path="/gps-tracking" element={<GpsTrackingPage />} />
                  <Route path="/gps-tracking/vergelijken" element={<VergelijkingPage />} />
                  <Route path="/trackjack-alternatief" element={<AlternatiefPage competitor="trackjack" />} />
                  <Route path="/fleetgo-alternatief" element={<AlternatiefPage competitor="fleetgo" />} />
                  <Route path="/gps-tracking/bedrijfswagen" element={<GpsTrackingSubPage subslug="bedrijfswagen" />} />
                  <Route path="/gps-tracking/bestelwagen" element={<GpsTrackingSubPage subslug="bestelwagen" />} />
                  <Route path="/gps-tracking/vrachtwagen" element={<GpsTrackingSubPage subslug="vrachtwagen" />} />
                  <Route path="/gps-tracking/voertuig-volgsysteem" element={<GpsTrackingSubPage subslug="voertuig-volgsysteem" />} />
                  <Route path="/fleet-management" element={<GenericServicePage serviceType="fleet" />} />
                  <Route path="/products" element={<Navigate to="/hardware/" replace />} />
                  <Route path="/hardware" element={<ProductsPage />} />
                  <Route path="/prijzen" element={<PrijzenPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/over-ons" element={<AboutPage />} />
                  <Route path="/besparingscalculator" element={<BesparingscalculatorPage />} />
                  <Route path="/offerte" element={<OffertePage />} />
                  {/* E-commerce verwijderd — edge 301 in _redirects is primair, dit is client-side vangnet */}
                  <Route path="/winkelwagen" element={<Navigate to="/hardware/" replace />} />

                  {/* Legal pages */}
                  <Route path="/algemene-voorwaarden" element={<LegalPages type="terms" />} />
                  <Route path="/privacybeleid" element={<LegalPages type="privacy" />} />
                  <Route path="/cookie-beleid" element={<LegalPages type="cookies" />} />
                  {/* Privacy alias used in email templates */}
                  <Route path="/privacy" element={<LegalPages type="privacy" />} />

                  {/* Unsubscribe page — called via email link */}
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />

                  {/* City SEO landing pages */}
                  {CITIES.map(city => (
                    <Route key={city} path={`/${city}`} element={<CityLandingPage city={city} />} />
                  ))}

                  {/* Industry sub-pages - Rittenregistratie */}
                  {INDUSTRIES_RITTEN.map(ind => (
                    <Route key={ind.slug} path={`/rittenregistratie/${ind.slug}`} element={<IndustryLandingPage parent="rittenregistratie" slug={ind.slug} name={ind.name} />} />
                  ))}

                  {/* Industry sub-pages - Fleet Management */}
                  {INDUSTRIES_FLEET.map(ind => (
                    <Route key={ind.slug} path={`/fleet-management/${ind.slug}`} element={<IndustryLandingPage parent="fleet-management" slug={ind.slug} name={ind.name} />} />
                  ))}

                  {/* FAQ landing pages — Phase 1B */}
                  <Route path="/rrs-keurmerk-lijst-2026" element={<RrsKeurmerkLijst2026Page />} />
                  <Route path="/avg-rittenregistratie-werknemer" element={<AvgRittenregistratieWerknemerPage />} />
                  <Route path="/rittenregistratie-zonder-kastje" element={<RittenregistratieZonderKastjePage />} />
                  <Route path="/rittenregistratie-sjabloon" element={<RittenregistratieSjabloonPage />} />
                  <Route path="/rittenregistratie-regels-index" element={<RittenregistratieRegelsIndexPage />} />
                  <Route path="/gps-tracker-zonder-abonnement-zakelijk" element={<GpsTrackerZonderAbonnementZakelijkPage />} />
                  <Route path="/wagenparkbeheer-kosten-berekenen" element={<WagenparkbeheerKostenPage />} />

                  {/* Problem-solver landing pages — Phase 1B batch-2 */}
                  <Route path="/bestelwagen-gestolen-wat-nu" element={<BestelwagenGestolenWatNuPage />} />
                  <Route path="/diesel-diefstal-voorkomen" element={<DieselDieftalVoorkomPage />} />
                  <Route path="/vca-rittenregistratie-eis" element={<VcaRittenregistratieEisPage />} />
                  <Route path="/gps-tracker-bouwbedrijf-werknemer-avg" element={<GpsTrackerBouwbedrijfWerknemerAvgPage />} />
                  <Route path="/bestelwagen-huren-met-gps" element={<BestelwagenHurenMetGpsPage />} />

                  {/* Problem-solver landing pages — Phase 3D batch-2 (Wave-9 W9b) */}
                  <Route path="/gps-tracker-uitbouwen-privacy" element={<GpsTrackerUitbouwenPrivacyPage />} />
                  <Route path="/zzp-rittenadministratie-zonder-werkgever" element={<ZzpRittenadministratieZonderWerkgeverPage />} />

                  {/* Problem-solver landing pages — Phase 3D batch-3 (Wave-10 W10b) */}
                  <Route path="/gps-tracker-zonder-bijtelling-2026" element={<GpsTrackerZonderBijtelling2026Page />} />
                  <Route path="/tracker-installeren-leasewagen-toestemming" element={<TrackerInstallerenLeasewagenToestemmingPage />} />
                  <Route path="/gps-data-bewaartermijn" element={<GpsDataBewaartermijnPage />} />
                  <Route path="/werknemer-gps-toestemming-vragen" element={<WerknemerGpsToestemmingVragenPage />} />
                  <Route path="/gps-onnauwkeurig-locatie-fix" element={<GpsOnnauwkeurigLocatieFixPage />} />

                  {/* Problem-solver landing pages — Phase 3D batch-4 (P3.D) */}
                  <Route path="/gps-tracker-zonder-export-kosten" element={<GpsTrackerZonderExportKostenPage />} />
                  <Route path="/wpm-rapportage" element={<WpmLanding />} />
                  <Route path="/wagenparkdata-eigendom-driver" element={<WagenparkdataEigendomDriverPage />} />
                  <Route path="/gps-schending-werknemerrechten-claim" element={<GpsSchendingWerknemerrechtenClaimPage />} />
                  <Route path="/ritgegevens-belastingcontrole-avg" element={<RitgegevensBelastingcontroleAvgPage />} />
                  <Route path="/gps-tracker-voertuigverkoop-verwijdering" element={<GpsTrackerVoertuigverkoopVerwijderingPage />} />

                  {/* Vehicle-type GPS tracker pages — Phase 2C */}
                  <Route path="/gps-tracker" element={<GpsTrackerIndexPage />} />
                  <Route path="/gps-tracker/:type" element={<GpsTrackerVehiclePage />} />

                  {/* Glossary cluster — Phase 3E */}
                  <Route path="/woordenboek" element={<WoordenboekIndexPage />} />
                  <Route path="/woordenboek/:term" element={<WoordenboekTermPage />} />

                  {/* Vacatures */}
                  <Route path="/vacatures" element={<AboutPage section="vacatures" />} />

                  {/* OAuth Callback */}
                  <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

                  {/* Instagram bio link hub — noindex */}
                  <Route path="/ig" element={<IgHub />} />

                  {/* Internal Tools */}
                  <Route path="/partner/backlink-toolkit" element={<BacklinkToolkitPage />} />

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <LarsVoiceAgent />
            <CookieConsent />
            <StickyConversionBar />
          </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;

